import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ProfessionalLayout from "@/components/layout/ProfessionalLayout";
import api from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, CalendarDays, CheckCircle, Clock, Phone, Pill, RefreshCw, Search, XCircle, AlertTriangle } from "lucide-react";

const STATUS = {
  taken: { label: "Pris", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle },
  not_taken: { label: "Non pris", cls: "bg-red-50 text-red-800 border-red-200", icon: XCircle },
  partial: { label: "Partiel", cls: "bg-orange-50 text-orange-800 border-orange-200", icon: AlertTriangle },
  missed: { label: "Non coché", cls: "bg-amber-50 text-amber-800 border-amber-200", icon: Clock },
  pending: { label: "À cocher", cls: "bg-blue-50 text-blue-800 border-blue-200", icon: Clock },
  upcoming: { label: "Prévu", cls: "bg-slate-50 text-slate-700 border-slate-200", icon: CalendarDays },
};

const HISTORY_OPTIONS = [7, 14, 30];

export default function NurseOralAdherence() {
  const { user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [savingDoseId, setSavingDoseId] = useState(null);
  const [query, setQuery] = useState("");
  const [historyDays, setHistoryDays] = useState(30);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const res = await api.get("/oral-adherence/ide/patients");
      const items = res.data?.items || [];
      setPatients(items);
      if (!selected && items.length > 0) {
        await openPatient(items[0], false, historyDays);
      }
    } catch (err) {
      toast.error("Erreur chargement observance per os");
    } finally {
      setLoading(false);
    }
  };

  const openPatient = async (patient, showToast = true, days = historyDays) => {
    try {
      setSelected(patient);
      setCalendarLoading(true);
      const res = await api.get(`/oral-adherence/ide/patients/${patient.patientId}/calendar?history_days=${days}&future_days=14`);
      setCalendar(res.data || null);
    } catch (err) {
      if (showToast) toast.error("Erreur chargement calendrier patient");
    } finally {
      setCalendarLoading(false);
    }
  };

  const validateDose = async (dose, status) => {
    if (!selected?.patientId || dose.isVirtual || !dose.id) return;
    try {
      setSavingDoseId(dose.id);
      await api.post(`/oral-adherence/${dose.id}/nurse-validate`, {
        status,
        reason: status === "taken" ? null : "other",
      });
      toast.success(status === "taken" ? "Prise validée" : status === "not_taken" ? "Prise marquée non prise" : "Prise marquée partielle");
      await openPatient(selected, false, historyDays);
      const res = await api.get("/oral-adherence/ide/patients");
      setPatients(res.data?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de valider la prise");
    } finally {
      setSavingDoseId(null);
    }
  };

  useEffect(() => { fetchPatients(); }, []);
  useEffect(() => {
    if (selected?.patientId) openPatient(selected, false, historyDays);
  }, [historyDays]);

  const filteredPatients = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter((p) => [p.patientName, p.cancerType, ...(p.treatments || []).map(t => t.molecule)]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(q)));
  }, [patients, query]);

  const attentionCount = patients.filter(p => p.needsAttention).length;
  const isDoctor = user?.role === "MEDECIN";
  const dashboardPath = isDoctor ? "/doctor/dashboard" : "/nurse/dashboard";

  return (
    <ProfessionalLayout title="Observance per os">
      <div className="space-y-5" data-testid="nurse-oral-adherence-page">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Link to={dashboardPath} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-emerald-700" data-testid="oral-adherence-back-dashboard">
              <ArrowLeft className="h-4 w-4" /> Retour dashboard
            </Link>
            <h1 className="mt-2 text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Patients sous traitement per os
            </h1>
            <p className="mt-1 text-sm text-slate-500" data-testid="oral-adherence-subtitle">
              Triés automatiquement : toute prise non cochée ou non prise sur la semaine précédente passe en premier.
            </p>
          </div>
          <button
            onClick={fetchPatients}
            data-testid="oral-adherence-refresh-button"
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
          >
            <RefreshCw className="h-4 w-4" /> Actualiser
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="oral-adherence-summary-cards">
          <SummaryCard label="Patients per os" value={patients.length} tone="slate" />
          <SummaryCard label="À prioriser" value={attentionCount} tone="amber" />
          <SummaryCard label="À jour" value={patients.length - attentionCount} tone="emerald" />
          <SummaryCard label="Score risque" value="7 j" tone="blue" />
        </div>

        <div className="grid gap-4 lg:grid-cols-[0.95fr_1.35fr]">
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="oral-adherence-patient-list-section">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher patient ou molécule..."
                data-testid="oral-adherence-search-input"
                className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm outline-none transition focus:border-emerald-300 focus:bg-white focus:ring-2 focus:ring-emerald-100"
              />
            </div>
            {loading ? (
              <div className="py-14 text-center text-sm text-slate-400" data-testid="oral-adherence-list-loading">Chargement...</div>
            ) : filteredPatients.length === 0 ? (
              <div className="py-14 text-center text-sm text-slate-400" data-testid="oral-adherence-list-empty">Aucun patient per os.</div>
            ) : (
              <div className="space-y-2" data-testid="oral-adherence-patient-list">
                {filteredPatients.map((patient) => (
                  <PatientRow key={patient.patientId} patient={patient} selected={selected?.patientId === patient.patientId} onClick={() => openPatient(patient)} />
                ))}
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="oral-adherence-calendar-section">
            {!selected ? (
              <div className="py-16 text-center text-sm text-slate-400" data-testid="oral-adherence-calendar-empty">Sélectionnez un patient.</div>
            ) : (
              <PatientCalendar patient={selected} calendar={calendar} loading={calendarLoading} historyDays={historyDays} onHistoryDaysChange={setHistoryDays} onValidateDose={validateDose} savingDoseId={savingDoseId} />
            )}
          </section>
        </div>
      </div>
    </ProfessionalLayout>
  );
}

function SummaryCard({ label, value, tone }) {
  const colors = {
    slate: "bg-slate-50 text-slate-800 border-slate-200",
    amber: "bg-amber-50 text-amber-800 border-amber-100",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-100",
    blue: "bg-blue-50 text-blue-800 border-blue-100",
  };
  return (
    <div className={`rounded-2xl border p-4 ${colors[tone]}`} data-testid={`oral-adherence-summary-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-xs font-bold uppercase opacity-75">{label}</p>
      <p className="mt-2 text-3xl font-black leading-none">{value}</p>
    </div>
  );
}

function PatientRow({ patient, selected, onClick }) {
  const moleculeLabel = (patient.treatments || []).map(t => t.molecule).filter(Boolean).join(" · ");
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`oral-adherence-patient-${patient.patientId}`}
      className={`w-full rounded-xl border p-3 text-left transition ${selected ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white hover:border-emerald-200 hover:bg-slate-50"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-black text-slate-900" data-testid={`oral-adherence-patient-name-${patient.patientId}`}>{patient.patientName}</p>
          <p className="mt-0.5 truncate text-xs font-semibold text-emerald-700" data-testid={`oral-adherence-patient-molecules-${patient.patientId}`}>{moleculeLabel || "Traitement per os"}</p>
          <p className="mt-0.5 text-xs text-slate-400">{patient.cancerType || "Pathologie non renseignée"}</p>
        </div>
        <span className={`rounded-full border px-2 py-1 text-[11px] font-black ${patient.needsAttention ? "border-amber-200 bg-amber-50 text-amber-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`} data-testid={`oral-adherence-risk-${patient.patientId}`}>
          {patient.needsAttention ? "À prioriser" : "À jour"}
        </span>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3 text-xs text-slate-500">
        <span data-testid={`oral-adherence-rate-${patient.patientId}`}>{patient.adherenceRate7d}% sur 7 j</span>
        <span data-testid={`oral-adherence-issues-${patient.patientId}`}>{patient.issueCount7d} prise(s) à revoir</span>
      </div>
    </button>
  );
}

function PatientCalendar({ patient, calendar, loading, historyDays, onHistoryDaysChange, onValidateDose, savingDoseId }) {
  const items = calendar?.items || [];
  return (
    <div data-testid="oral-adherence-selected-calendar">
      <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-black text-slate-900" data-testid="oral-adherence-selected-patient-name">{patient.patientName}</h2>
          <p className="mt-1 text-sm text-slate-500" data-testid="oral-adherence-selected-patient-meta">{patient.cancerType || "Pathologie non renseignée"}</p>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="oral-adherence-selected-treatments">
            {(patient.treatments || []).map((t) => <span key={t.id} className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-800"><Pill className="mr-1 inline h-3.5 w-3.5" />{t.molecule}</span>)}
          </div>
          <p className="mt-2 text-xs font-semibold text-slate-500" data-testid="oral-adherence-calendar-window-label">
            Historique affiché : {calendar?.pastWindowDays || historyDays} jours · prochaines prises : {calendar?.futureWindowDays || 14} jours
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="oral-adherence-history-controls">
          {HISTORY_OPTIONS.map((days) => (
            <button
              key={days}
              type="button"
              onClick={() => onHistoryDaysChange(days)}
              data-testid={`oral-adherence-history-${days}-days-button`}
              className={`min-h-[40px] rounded-xl border px-3 py-2 text-xs font-black transition ${historyDays === days ? "border-emerald-500 bg-emerald-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
            >
              {days} j
            </button>
          ))}
          {patient.patientPhone ? (
            <a href={`tel:${patient.patientPhone}`} className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50" data-testid="oral-adherence-selected-call-button">
              <Phone className="h-4 w-4" /> Appeler
            </a>
          ) : null}
        </div>
      </div>
      {loading ? (
        <div className="py-14 text-center text-sm text-slate-400" data-testid="oral-adherence-calendar-loading">Chargement calendrier...</div>
      ) : items.length === 0 ? (
        <div className="py-14 text-center text-sm text-slate-400" data-testid="oral-adherence-calendar-no-items">Aucune prise planifiée.</div>
      ) : (
        <div className="mt-4 grid gap-2" data-testid="oral-adherence-calendar-list">
          {items.map((dose) => <DoseRow key={dose.id} dose={dose} onValidateDose={onValidateDose} savingDoseId={savingDoseId} />)}
        </div>
      )}
    </div>
  );
}

function DoseRow({ dose, onValidateDose, savingDoseId }) {
  const meta = STATUS[dose.status] || STATUS.pending;
  const Icon = meta.icon;
  const scheduledDate = dose.scheduledDate ? new Date(`${dose.scheduledDate}T00:00:00`) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const canAct = Boolean(scheduledDate && scheduledDate <= today && !dose.isVirtual);
  const saving = savingDoseId === dose.id;
  return (
    <div className="grid gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-3 sm:grid-cols-[0.8fr_1fr_0.9fr] sm:items-center" data-testid={`oral-adherence-dose-${dose.id}`}>
      <div className="text-sm font-black text-slate-800" data-testid={`oral-adherence-dose-date-${dose.id}`}>{formatDate(dose.scheduledDate)}</div>
      <div className="min-w-0">
        <p className="truncate text-sm font-bold text-slate-800" data-testid={`oral-adherence-dose-molecule-${dose.id}`}>{dose.molecule}</p>
        <p className="text-xs text-slate-400">{frequencyLabel(dose.frequency)}</p>
      </div>
      <div className="space-y-2" data-testid={`oral-adherence-dose-control-${dose.id}`}>
        <span className={`inline-flex w-full items-center justify-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-black ${meta.cls}`} data-testid={`oral-adherence-dose-status-${dose.id}`}>
          <Icon className="h-3.5 w-3.5" /> {meta.label}
        </span>
        {canAct ? (
          <div className="grid grid-cols-3 gap-1.5" data-testid={`oral-adherence-dose-actions-${dose.id}`}>
            <ActionButton dose={dose} status="taken" label="Pris" tone="emerald" saving={saving} onValidateDose={onValidateDose} />
            <ActionButton dose={dose} status="not_taken" label="Non pris" tone="red" saving={saving} onValidateDose={onValidateDose} />
            <ActionButton dose={dose} status="partial" label="Partiel" tone="orange" saving={saving} onValidateDose={onValidateDose} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ActionButton({ dose, status, label, tone, saving, onValidateDose }) {
  const active = dose.status === status;
  const tones = {
    emerald: active ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50",
    red: active ? "bg-red-600 text-white border-red-600" : "bg-white text-red-700 border-red-200 hover:bg-red-50",
    orange: active ? "bg-orange-600 text-white border-orange-600" : "bg-white text-orange-700 border-orange-200 hover:bg-orange-50",
  };
  return (
    <button
      type="button"
      disabled={saving || active}
      onClick={() => onValidateDose(dose, status)}
      data-testid={`oral-adherence-dose-${status}-button-${dose.id}`}
      className={`min-h-[34px] rounded-lg border px-2 text-[11px] font-black transition-[background-color,color,opacity,transform] active:scale-[0.97] disabled:opacity-55 ${tones[tone]}`}
    >
      {saving ? "..." : label}
    </button>
  );
}

function formatDate(value) {
  if (!value) return "—";
  return new Date(`${value}T00:00:00`).toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" });
}

function frequencyLabel(value) {
  return { daily: "Quotidien", every_other_day: "Un jour sur deux", weekly: "Hebdomadaire" }[value] || value || "";
}